// REK-1811 (QA fix): question-ordering logic for the profile wizard.
//
// Extracted as a pure, dependency-free function so the "never-seen before
// previously-skipped" resume behavior can be unit-tested without rendering the
// wizard or touching Supabase.

export interface WizardPosition {
  stepIndex: number;
  fieldIndex: number;
}

/**
 * Minimal structural shape of a wizard step needed for ordering. The full
 * `ProfileStep` in ProfileWizardContext is assignable to this.
 */
export interface OrderableStep {
  fields: ReadonlyArray<{ questionMappingId: string }>;
}

/**
 * Two-pass scan for the next question to present, starting at
 * (startStepIndex, startFieldIndex) and walking forward through steps/fields.
 *
 * - Pass 1 prefers a *never-seen* question: one that is neither answered nor
 *   previously skipped.
 * - Pass 2 falls back to any *unanswered* question. Once pass 1 is exhausted,
 *   the only unanswered questions left are previously-skipped ones, so pass 2
 *   surfaces them.
 *
 * This is what makes a resume (scan from 0,0) land on a fresh question instead
 * of re-prompting something the user already chose to skip. Skip state is an
 * ordering hint ONLY -- `isSkipped` never marks a question answered, so points,
 * payouts, dedup, and progress (all keyed on real `user_knowledge` rows) are
 * unaffected.
 *
 * Returns null when no unanswered question exists at or after the start
 * position (i.e. everything forward is answered).
 *
 * REK-1916: an optional `isGatedOut` predicate excludes conditionally-gated
 * questions (e.g. child questions for a non-parent) from BOTH passes, so the
 * wizard never lands on a question the user shouldn't be shown.
 */
export function findNextUnansweredPosition(
  steps: ReadonlyArray<OrderableStep>,
  startStepIndex: number,
  startFieldIndex: number,
  isAnswered: (questionMappingId: string) => boolean,
  isSkipped: (questionMappingId: string) => boolean,
  isGatedOut: (questionMappingId: string) => boolean = () => false,
): WizardPosition | null {
  const scan = (preferUnseen: boolean): WizardPosition | null => {
    for (let si = startStepIndex; si < steps.length; si++) {
      const firstField = si === startStepIndex ? startFieldIndex : 0;
      const fields = steps[si].fields;
      for (let fi = firstField; fi < fields.length; fi++) {
        const id = fields[fi].questionMappingId;
        // A gated-out question is not presentable regardless of answer/skip
        // state -> skip it in both passes.
        if (isGatedOut(id)) continue;
        if (isAnswered(id)) continue;
        if (preferUnseen && isSkipped(id)) continue;
        return { stepIndex: si, fieldIndex: fi };
      }
    }
    return null;
  };

  return scan(true) ?? scan(false);
}

/**
 * Walk BACKWARD from (startStepIndex, startFieldIndex) to the nearest earlier
 * question that is not gated out. Used by the wizard's Back button so it never
 * lands on a conditionally-gated question that forward navigation skips
 * (REK-1916). Returns null when there is no earlier visible question.
 */
export function findPreviousVisiblePosition(
  steps: ReadonlyArray<OrderableStep>,
  startStepIndex: number,
  startFieldIndex: number,
  isGatedOut: (questionMappingId: string) => boolean = () => false,
): WizardPosition | null {
  let si = startStepIndex;
  let fi = startFieldIndex;
  while (si > 0 || fi > 0) {
    if (fi > 0) {
      fi -= 1;
    } else {
      si -= 1;
      fi = steps[si].fields.length - 1;
    }
    if (fi < 0) continue; // empty step -> keep walking back
    if (!isGatedOut(steps[si].fields[fi].questionMappingId)) {
      return { stepIndex: si, fieldIndex: fi };
    }
  }
  return null;
}
